/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useTranslation } from 'react-i18next';

import { Checkbox, Shimmer, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio';

import { BrandsNoData } from '../BrandsNoData';

import { BrandsAssignState, isEmpty } from './BrandsAssign.state';

interface RowProps {
  brand: { id: number; name: string };
  onClickCheckbox: () => void;
  checked?: boolean;
}

function LoadingRows() {
  return Array.from({ length: 3 }, (_, i) => (
    <div key={i}>
      <div className="flex h-16 items-center px-4">
        <Shimmer size="lg" />
      </div>
      <hr />
    </div>
  ));
}

function Row({ brand, onClickCheckbox, checked }: RowProps) {
  return (
    <div
      onClick={onClickCheckbox}
      className={twMerge(
        'flex h-16 cursor-pointer items-center justify-between gap-2 rounded-md px-4',
        checked ? 'bg-light-blue-50' : 'hover:bg-neutral-50',
      )}
    >
      <Text variant="subtitle-2" className="truncate">
        {brand.name}
      </Text>
      <Checkbox size="lg" checked={checked} onChange={onClickCheckbox} onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

function BrandSelector({ state = BrandsAssignState }) {
  const { t } = useTranslation('library');
  const { sortedSelectedBrandList, sortedUnselectedBrandList, isLoadingList, searchFilter } = useSnapshot(state);
  const { handleClickBrandCheckbox } = state;

  const isEmptyList = isEmpty('brand', state);

  const isFilteredListEmpty = sortedUnselectedBrandList?.length === 0 && sortedSelectedBrandList?.length === 0;

  return (
    <div data-testid="brand-selector">
      {isEmptyList && (
        <BrandsNoData
          title={t('brands.notListedYet.title', { subject: 'brand' })}
          description={t('brands.notListedYet.description', { subject: 'brand' })}
        />
      )}
      {!isEmptyList && (
        <>
          <div className="flex flex-col gap-1">
            {sortedSelectedBrandList.map((brand) => (
              <Row key={brand.id} brand={brand} onClickCheckbox={() => handleClickBrandCheckbox(brand, false)} checked />
            ))}
          </div>
          <div>
            {sortedUnselectedBrandList.map((brand) => (
              <div key={brand.id}>
                <Row key={brand.id} brand={brand} onClickCheckbox={() => handleClickBrandCheckbox(brand, true)} />
                <hr />
              </div>
            ))}
            {isLoadingList && <LoadingRows />}
          </div>
        </>
      )}
      {!isEmptyList && isFilteredListEmpty && searchFilter && (
        <BrandsNoData
          title={t('brands.noKeywordFound.title', { keyword: searchFilter })}
          description={t('brands.noKeywordFound.description')}
        />
      )}
    </div>
  );
}

export { BrandSelector };
